/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { css, cssMap, jsx, styled } from '@compiled/react';
import { useIntl } from 'react-intl-next';

import { cx } from '@atlaskit/css';
import Image from '@atlaskit/image';
import { Inline } from '@atlaskit/primitives/compiled';
import Pressable from '@atlaskit/primitives/pressable';
import {
	CustomBlock,
	MediaPlacement,
	PreviewBlock,
	SmartLinkSize,
	SmartLinkTheme,
	TitleBlock,
} from '@atlaskit/smart-card';
import { CardSSR as SmartCardSSR } from '@atlaskit/smart-card/ssr';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { token } from '@atlaskit/tokens';
import { useDarkModeColors } from '@atlassian/color-picker';
import {
	CommonLayout,
	getSmartCardFallbackUrl,
	useProductDependencies,
	useTrackedSmartCardState,
} from '@atlassian/company-hub-common';
import {
	CustomImageUploadRenderer,
	getImageTypeToShow,
} from '@atlassian/company-hub-common/media-components';

import { useResolveImage } from '../../../controllers/use-resolve-image';
import { type CommonFrameProps, framesDimensions, FrameSizes } from '../../../types';
import {
	CommonCarouselWrapperStyles as CommonCarouselWrapperStylesOld,
	NavigationThumbnailWrapper as NavigationThumbnailWrapperOld,
} from '../styled';

import { messages } from './messages';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const NavigationThumbnailWrapper = styled.div<{
	hasRoundedCorners?: boolean;
	height: number;
	isSelected?: boolean;
	maxWidth: number;
	minWidth: number;
}>({
	overflow: 'hidden',
	flexShrink: 1,
	flexDirection: 'row',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	height: ({ height }) => `${height}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	minWidth: ({ minWidth }) => `${minWidth}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	maxWidth: ({ maxWidth }) => `${maxWidth}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	borderRadius: ({ hasRoundedCorners }) => (hasRoundedCorners ? '4px' : 0),
	borderStyle: 'solid',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	borderColor: ({ isSelected }) =>
		isSelected ? token('color.border.selected') : token('color.background.neutral'),
});

const styles = cssMap({
	pressable: {
		paddingTop: token('space.negative.025'),
		paddingBottom: token('space.negative.025'),
		paddingLeft: token('space.negative.150'),
		width: '100%',
		backgroundColor: token('color.background.input.pressed'),
	},
	container: {
		height: '40px',
	},
	selectedToEdit: {
		outline: `${token('border.width.focused')} solid ${token('color.border.focused')}`,
	},
	selectedToRemove: {
		outline: `${token('border.width.focused')} solid ${token('color.border.danger')}`,
	},
	image: {
		width: '48px',
		height: '40px',
	},
	textWrapper: {
		paddingLeft: token('space.100'),
		paddingTop: token('space.050'),
		paddingBottom: token('space.050'),
	},
});

const textStyles = css({
	textAlign: 'start',
	margin: 0,
	textWrap: 'wrap',
	font: token('font.body'),
	// Match old thumbnail behaviour: max 2 lines (TitleBlock uses maxLines={2})
	display: '-webkit-box',
	WebkitLineClamp: 2,
	WebkitBoxOrient: 'vertical',
	overflow: 'hidden',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors, @atlaskit/design-system/no-nested-styles -- Ignored via go/DSP-18766
	'&&': {
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: 1.25,
	},
});

type NavigationThumbnailProps = {
	hasRoundedCorners?: boolean;
	index: number;
	isSelected?: boolean;
	isSelectedToEdit?: boolean;
	isSelectedToRemove?: boolean;
	maxWidth?: number;
	minWidth?: number;
	setSelectedHeroFrameIndex: (index: number) => void;
} & CommonFrameProps;

export const NavigationThumbnail = ({
	cardId,
	link,
	title,
	imageSrc,
	isSelected,
	contentId,
	isEmpty,
	minWidth,
	maxWidth,
	wrapperHeight,
	hasRoundedCorners,
	layout,
	backgroundColor: savedBackgroundColor,
	onFailed,
	onSucceeded,
	setSelectedHeroFrameIndex,
	index,
	isSelectedToEdit,
	isSelectedToRemove,
}: NavigationThumbnailProps): JSX.Element => {
	const { isRestricted, isUnauthorized, trackAndCategorizeError, trackSuccess } =
		useTrackedSmartCardState(cardId, link, onSucceeded, onFailed);
	const intl = useIntl();
	const { isSSRProductInDarkMode } = useProductDependencies();

	const displayImageSrc = useResolveImage(link, imageSrc);
	const { showUnsplashImage, showExternalSiteImage, showCustomUploadedImage } = getImageTypeToShow({
		imageSrc: displayImageSrc,
	});
	const { getDarkModeCompatibleBackgroundColor } = useDarkModeColors(isSSRProductInDarkMode);
	const backgroundColor = getDarkModeCompatibleBackgroundColor(savedBackgroundColor ?? '#FFFFFF');
	const substituteBackgroundColorForImage =
		layout === CommonLayout.NO_IMAGE_BACKGROUND && backgroundColor;

	const showCustomTitleUnauthed = isUnauthorized && !!title;
	const showCustomTitleRestricted = isRestricted && !!title;

	const fallbackUrl = getSmartCardFallbackUrl();

	const oldThumbnail = () => (
		<CommonCarouselWrapperStylesOld isSelected={isSelected}>
			<NavigationThumbnailWrapperOld
				minWidth={minWidth ?? framesDimensions[FrameSizes.NAVIGATION_THUMBNAIL].minWidth}
				maxWidth={maxWidth ?? framesDimensions[FrameSizes.NAVIGATION_THUMBNAIL].maxWidth}
				height={wrapperHeight ?? framesDimensions[FrameSizes.NAVIGATION_THUMBNAIL].height}
				hasRoundedCorners={hasRoundedCorners}
			>
				<SmartCardSSR
					id={cardId}
					appearance="inline"
					frameStyle="show"
					platform="web"
					ui={{
						theme: SmartLinkTheme.Black,
						clickableContainer: false,
						size: SmartLinkSize.Large,
					}}
					url={link || fallbackUrl}
					onResolve={trackSuccess}
					onError={trackAndCategorizeError}
				>
					{substituteBackgroundColorForImage && (
						<CustomBlock>
							<div
								css={[customBackgroundStyles.backgroundColorOnly]}
								style={{ backgroundColor }}
								data-testid="backgroundColorBlock"
							/>
						</CustomBlock>
					)}
					{!substituteBackgroundColorForImage && (showUnsplashImage || showExternalSiteImage) && (
						<PreviewBlock
							ignoreContainerPadding
							placement={MediaPlacement.Left}
							overrideUrl={displayImageSrc}
						/>
					)}
					{!substituteBackgroundColorForImage && showCustomUploadedImage ? (
						<CustomBlock css={[customImageUploadStyles, carouselV2CustomImageWidth]}>
							<CustomImageUploadRenderer contentId={contentId} imageSrc={displayImageSrc} />
						</CustomBlock>
					) : null}
					<TitleBlock
						hideIcon
						text={
							// a link with no title should expect undefined
							isEmpty ? title || intl.formatMessage(messages.emptyStatePlaceholderTitle) : title
						}
						maxLines={2}
						anchorTarget="_self"
						css={[
							titleBlockStylesV2,
							(showCustomTitleUnauthed || showCustomTitleRestricted) &&
								titleBlockHideUnauthorizedStyles,
							isEmpty && !title
								? titleBlockLinkTextSubtlestColorStyles
								: titleBlockLinkTextColorStyles,
						]}
					/>
				</SmartCardSSR>
			</NavigationThumbnailWrapperOld>
		</CommonCarouselWrapperStylesOld>
	);

	return expValEquals('company_hub_carousel_thumbnails-refactor', 'isEnabled', true) ? (
		<NavigationThumbnailWrapper
			minWidth={minWidth ?? framesDimensions[FrameSizes.NAVIGATION_THUMBNAIL].minWidth}
			maxWidth={maxWidth ?? framesDimensions[FrameSizes.NAVIGATION_THUMBNAIL].maxWidth}
			height={wrapperHeight ?? framesDimensions[FrameSizes.NAVIGATION_THUMBNAIL].height}
			hasRoundedCorners={hasRoundedCorners}
			isSelected={isSelected}
		>
			<Pressable
				testId="thumbnail"
				xcss={cx(
					styles.pressable,
					isSelectedToEdit && styles.selectedToEdit,
					isSelectedToRemove && styles.selectedToRemove,
				)}
				onClick={() => setSelectedHeroFrameIndex(index)}
			>
				<Inline alignBlock="center" alignInline="start" xcss={styles.container}>
					{showCustomUploadedImage ? (
						<div css={styles.image}>
							<CustomImageUploadRenderer contentId={contentId} imageSrc={displayImageSrc} />
						</div>
					) : displayImageSrc ? (
						<Image src={displayImageSrc} alt="" css={styles.image} />
					) : (
						<div css={styles.image} aria-hidden />
					)}
					<div css={styles.textWrapper}>
						{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
						<p css={textStyles}>
							{title || intl.formatMessage(messages.emptyStatePlaceholderTitle)}
						</p>
					</div>
				</Inline>
			</Pressable>
		</NavigationThumbnailWrapper>
	) : (
		oldThumbnail()
	);
};

const titleBlockLinkTextColorStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles -- Ignored via go/DSP-18766
	'[data-smart-element-link]': {
		color: token('color.text'),
	},
});

const titleBlockLinkTextSubtlestColorStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles -- Ignored via go/DSP-18766
	'[data-smart-element-link]': {
		color: token('color.text.subtlest'),
	},
});

const titleBlockHideUnauthorizedStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles -- Ignored via go/DSP-18766
	'div[data-smart-element-group]': {
		display: 'none',
	},
});

const titleBlockStylesV2 = css({
	marginTop: token('space.0'),
	marginLeft: token('space.050'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles -- Ignored via go/DSP-18766
	'[data-smart-element-link]': {
		fontWeight: token('font.weight.regular'),
		flexShrink: 1,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles -- Ignored via go/DSP-18766
	'a, a:visited, a:focus, a:hover, a:active': {
		textDecoration: 'none',
		pointerEvents: 'none',
	},
	width: '100%',
});

const customImageUploadStyles = css({
	position: 'absolute',
	width: '72px',
	top: token('space.0'),
	bottom: token('space.0'),
	left: token('space.0'),
});

const customBackgroundStyles = cssMap({
	backgroundColorOnly: {
		position: 'absolute',
		width: 'var(--preview-block-width)',
		top: token('space.0'),
		bottom: token('space.0'),
		left: token('space.0'),
	},
});

const carouselV2CustomImageWidth = css({
	width: token('space.600'),
});
